import { InstanceBatch } from "../../core/batching/instance_batch.js";
import { AttributeSize, BlendMode } from "../../core/types/webgl_types.js";
import { ShaderBuilder } from "../../shader_builder/shader_builder.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Game } from "../../../game.js";

export const ConfettiLayout = {
  a_start_pos: AttributeSize.Vec2,
  a_velocity: AttributeSize.Vec2,
  a_color: AttributeSize.Vec3,
  a_angle_spin: AttributeSize.Vec2,
  a_frame_size: AttributeSize.Vec2,
  a_flutter: AttributeSize.Vec3,
  a_timing: AttributeSize.Vec2,
} as const;

export class VfxConfettiBatch extends InstanceBatch<typeof ConfettiLayout>
{
  public static readonly MAX_INSTANCES       : number = 300;
  public static readonly PARTICLE_SIZE       : number = 40;
  public static readonly ATLAS_COLS          : number = 3;
  public static readonly ATLAS_ROWS          : number = 3;
  public static readonly DRAG_K              : number = 0.0020;

  public constructor()
  {
    super( {
      max_instances: VfxConfettiBatch.MAX_INSTANCES,
      primitive_type: WebGL2RenderingContext.TRIANGLES,
      vertices_per_instance: 6,
      blend_mode: BlendMode.Normal,
      layout: ConfettiLayout,
    } );

    this.buildShader( new ShaderBuilder()
      .withUniform( 'sampler2D', 'u_texture' )
      .withUniform( 'float', 'u_scale' )
      .withUniform( 'float', 'u_screen_h' )
      .includeChunk( 'getSpriteUV' )
      .includeChunk( 'remap' )
      .withVarying( 'vec2', 'v_uv' )
      .withVarying( 'float', 'v_alpha' )
      .withVarying( 'vec3', 'v_color_1' )
      .withVertexLogic( `
        float elapsed = max( 0.0, u_time - a_timing.x );
        float t = clamp( elapsed / a_timing.y, 0.0, 1.0 );
        
        float scale = u_scale;
        
        // Air resistance drag decay for launch velocity
        float drag_k = ${VfxConfettiBatch.DRAG_K.toFixed( 4 )};
        float drag_factor = ( 1.0 - exp( -drag_k * elapsed ) ) / drag_k;

        // Screen-height adaptive fall speed (guarantees full-screen descent on all screens)
        float fall_speed = u_screen_h * 0.00023;
        float fall_displacement = fall_speed * elapsed;
        
        // Chaotic dual-frequency wind sway
        float main_sway = sin( a_flutter.x + elapsed * a_flutter.z );
        float sub_sway  = sin( a_flutter.x * 2.3 + elapsed * a_flutter.z * 1.7 ) * 0.35;
        float sway      = ( main_sway + sub_sway ) * a_flutter.y * scale;
        
        // Position: launch position + velocity decay + gravity fall displacement + horizontal sway
        position = a_start_pos + a_velocity * drag_factor + vec2( sway, fall_displacement );
        
        // 3D paper flutter & flip rotation
        float flip = cos( a_flutter.x * 2.0 + elapsed * a_flutter.z * 1.5 );
        angle = a_angle_spin.x + a_angle_spin.y * elapsed;
        
        // Size: width squashes periodically to simulate paper flipping in 3D
        size = vec2( a_frame_size.y * mix( 0.15, 1.0, abs( flip ) ), a_frame_size.y );

        vec2 min_uv;
        vec2 max_uv;

        getSpriteUV(
          int( a_frame_size.x + 0.5 ),
          vec2( textureSize( u_texture, 0 ) ),
          ivec2( ${VfxConfettiBatch.ATLAS_COLS}, ${VfxConfettiBatch.ATLAS_ROWS} ),
          vec2( 0.0 ),
          min_uv,
          max_uv
        );

        v_uv = mix( min_uv, max_uv, uv );
        
        // Fade out from 0.8 to 1.0 progress of lifespan
        v_alpha = ( u_time < a_timing.x ) ? 0.0 : remap( t, 0.8, 1.0, 1.0, 0.0, true );
        v_color_1 = a_color;
      ` )
      .withFragmentLogic( `
        vec4 tex = texture( u_texture, v_uv );

        float tex_lightness = dot( tex.rgb, vec3( 0.299, 0.587, 0.114 ) );
        vec3 shaded_color = v_color_1 * mix( 0.88, 1.22, tex_lightness );
        fragColor = vec4( tex.rgb * shaded_color * v_alpha, v_alpha * tex.a );
      ` ) );
  }

  protected override onInit( gl: WebGL2RenderingContext ): void
  {
    const texture = this.createTexture( ImagesHolder.getImage( ImgID.ConfettiAtlas ), gl, {
      min_filter: gl.LINEAR,
      mag_filter: gl.LINEAR,
      generate_mipmaps: false,
    } );
    this.textures.push( { location: "u_texture", texture: texture, unit: 0 } );
  }

  protected override onBeforeRender( gl: WebGL2RenderingContext ): void
  {
    gl.uniform1f( this.shader.getLocation( gl, "u_scale" ), LayoutScaler.getScale() );
    gl.uniform1f( this.shader.getLocation( gl, "u_screen_h" ), Game.getScreenHeight() );
  }

  public spawn(
    x: number, y: number,
    vel_x: number, vel_y: number,
    color_r: number, color_g: number, color_b: number,
    angle: number, spin: number, frame_index: number,
    life_span: number, start_time: number,
    flutter_phase: number, flutter_amp: number, flutter_freq: number
  ): void
  {
    const idx = this.allocateInstanceIndex( start_time, life_span );
    if ( idx === -1 ) return;

    const offset = idx * this.buffer.floats_per_instance;
    const gpu_data = this.buffer.getDataArray();

    gpu_data[offset + this.attr_offset.a_start_pos] = x;
    gpu_data[offset + this.attr_offset.a_start_pos + 1] = y;

    gpu_data[offset + this.attr_offset.a_velocity] = vel_x;
    gpu_data[offset + this.attr_offset.a_velocity + 1] = vel_y;

    gpu_data[offset + this.attr_offset.a_color] = color_r;
    gpu_data[offset + this.attr_offset.a_color + 1] = color_g;
    gpu_data[offset + this.attr_offset.a_color + 2] = color_b;

    gpu_data[offset + this.attr_offset.a_angle_spin] = angle;
    gpu_data[offset + this.attr_offset.a_angle_spin + 1] = spin;

    gpu_data[offset + this.attr_offset.a_frame_size] = frame_index;
    gpu_data[offset + this.attr_offset.a_frame_size + 1] = VfxConfettiBatch.PARTICLE_SIZE * LayoutScaler.getScale();

    gpu_data[offset + this.attr_offset.a_flutter] = flutter_phase;
    gpu_data[offset + this.attr_offset.a_flutter + 1] = flutter_amp;
    gpu_data[offset + this.attr_offset.a_flutter + 2] = flutter_freq;

    gpu_data[offset + this.attr_offset.a_timing] = start_time;
    gpu_data[offset + this.attr_offset.a_timing + 1] = life_span;
  }

  protected override updateInstance( _gpu_data: Float32Array, _offset: number, _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
